import { venueSupportsRfqMaker } from '@/lib/configs/venue-features'
import { AccountStatus } from '@/types/generated/common/account'
import { TradeVenue } from '@/types/generated/common/types'
import { RfqNetwork, RfqState } from '@/types/generated/rfq'
import {
  RfqMakerQuote,
  RfqMakerQuoteState,
  RfqMakerRfq,
  RfqMakerRfqList,
  RfqQuoteSide,
} from '@/types/generated/rfq'

/*
 * Pure state for the maker-side RFQ blotter (contract: oems_v2 docs/rfq-maker-frontend.md).
 *
 * One RFQ_MAKER stream per account carries two frame kinds: incoming RFQs raised by other
 * takers (`RfqMakerRfqList`) and this account's own quotes (`RfqMakerQuoteList`). Both are
 * "snapshot then deltas" with no full-snapshot flag, so everything here is an upsert:
 * RFQs by (account, rfq_id), quotes by quote_id, the larger `updatedAtMs` wins and an equal
 * stamp means "no change" (the stamp is stable across re-emits).
 */

/** Numeric compare of an int64 ms string; bad or empty input reads as 0. */
export function stampOf(value: string | undefined): bigint {
  try {
    return BigInt(value || '0')
  } catch {
    return 0n
  }
}

// ─── Incoming RFQs ─────────────────────────────────────────────────────────

/** An RFQ is account-scoped: the same venue RFQ reaches every maker account on that venue. */
export function makerRfqKey(rfq: { accountId: string; rfqId: string }): string {
  return `${rfq.accountId}|${rfq.rfqId}`
}

export function isTerminalMakerRfqState(state: RfqState): boolean {
  return (
    state === RfqState.RFQ_STATE_FILLED ||
    state === RfqState.RFQ_STATE_CANCELLED ||
    state === RfqState.RFQ_STATE_EXPIRED
  )
}

/** Terminal RFQs kept per account once they have left the live list (newest first). */
export const MAX_TERMINAL_MAKER_RFQS_PER_ACCOUNT = 200

/**
 * Merge incoming RFQ frames. Larger `updatedAtMs` replaces the row wholesale; an equal or
 * older stamp is dropped; a terminal RFQ never reverts to OPEN, whatever the stamp says.
 * Returns `current` itself when nothing changed, so an idle re-emit costs no re-render.
 */
export function upsertMakerRfqs(
  current: Record<string, RfqMakerRfq>,
  incoming: readonly RfqMakerRfq[]
): Record<string, RfqMakerRfq> {
  let next: Record<string, RfqMakerRfq> | null = null
  for (const frame of incoming) {
    if (!frame.rfqId || !frame.accountId) continue
    const key = makerRfqKey(frame)
    const existing = (next ?? current)[key]
    if (existing) {
      if (stampOf(frame.updatedAtMs) <= stampOf(existing.updatedAtMs)) continue
      if (isTerminalMakerRfqState(existing.state) && !isTerminalMakerRfqState(frame.state)) {
        continue
      }
    }
    next ??= { ...current }
    next[key] = frame
  }
  return next ? capTerminalMakerRfqs(next) : current
}

/** Bound the terminal RFQs per account; OPEN rows are never dropped. */
export function capTerminalMakerRfqs(
  rfqs: Record<string, RfqMakerRfq>,
  max = MAX_TERMINAL_MAKER_RFQS_PER_ACCOUNT
): Record<string, RfqMakerRfq> {
  const terminalByAccount = new Map<string, RfqMakerRfq[]>()
  for (const rfq of Object.values(rfqs)) {
    if (!isTerminalMakerRfqState(rfq.state)) continue
    const list = terminalByAccount.get(rfq.accountId) ?? []
    list.push(rfq)
    terminalByAccount.set(rfq.accountId, list)
  }
  let result: Record<string, RfqMakerRfq> | null = null
  for (const list of terminalByAccount.values()) {
    if (list.length <= max) continue
    list.sort((a, b) => compareStampsDesc(a.updatedAtMs, b.updatedAtMs))
    result ??= { ...rfqs }
    for (const dropped of list.slice(max)) delete result[makerRfqKey(dropped)]
  }
  return result ?? rfqs
}

function compareStampsDesc(a: string, b: string): number {
  const sa = stampOf(a)
  const sb = stampOf(b)
  return sa === sb ? 0 : sa > sb ? -1 : 1
}

/** An OPEN RFQ whose expiry has passed is dead even before the venue's EXPIRED frame lands. */
export function isMakerRfqLive(rfq: RfqMakerRfq, nowMs: number): boolean {
  if (rfq.state !== RfqState.RFQ_STATE_OPEN) return false
  const expires = Number(rfq.expiresAtMs)
  return !Number.isFinite(expires) || expires <= 0 || expires > nowMs
}

// ─── Maker availability ────────────────────────────────────────────────────

/**
 * The RFQ network an account makes on through its own venue: Deribit's Block RFQ, or the
 * venue's own RFQ system (UNSPECIFIED) everywhere else.
 */
export function nativeMakerNetwork(tradeVenue: TradeVenue): RfqNetwork {
  return tradeVenue === TradeVenue.TRADE_VENUE_DERIBIT
    ? RfqNetwork.RFQ_NETWORK_DERIBIT
    : RfqNetwork.RFQ_NETWORK_UNSPECIFIED
}

/** UNSPECIFIED reads as the venue's own network (it is also what a backend without `rfq_network` sends). */
export function makerNetwork(network: RfqNetwork, tradeVenue: TradeVenue): RfqNetwork {
  return network === RfqNetwork.RFQ_NETWORK_UNSPECIFIED ? nativeMakerNetwork(tradeVenue) : network
}

/**
 * Maker status, name and activity are per (account, network): a Deribit account linked to a
 * Paradigm desk is a maker on both, and can be refused on one while live on the other.
 */
export function makerKey(accountId: string, network: RfqNetwork): string {
  return `${accountId}|${network}`
}

type MakerListKey = Pick<RfqMakerRfqList, 'accountId' | 'tradeVenue' | 'rfqNetwork'>

function listKey(list: MakerListKey): string {
  return makerKey(list.accountId, makerNetwork(list.rfqNetwork, list.tradeVenue))
}

/** The `makerKey` of the account and network an RFQ or quote belongs to. */
export function makerKeyOf(row: Pick<RfqMakerRfq, 'accountId' | 'tradeVenue' | 'rfqNetwork'>) {
  return listKey(row)
}

export interface MakerUnavailable {
  tradeVenue: TradeVenue
  reason: string
}

/**
 * An `RfqMakerRfqList` with `makerUnavailableReason` set means the venue refused this
 * account as a maker on the list's network; any later list without it means access is back.
 * Keyed by `makerKey`.
 */
export function applyMakerAvailability(
  current: Record<string, MakerUnavailable>,
  list: MakerListKey & Pick<RfqMakerRfqList, 'makerUnavailableReason'>
): Record<string, MakerUnavailable> {
  if (!list.accountId) return current
  const key = listKey(list)
  const reason = list.makerUnavailableReason?.trim()
  const existing = current[key]
  if (reason) {
    if (existing && existing.reason === reason && existing.tradeVenue === list.tradeVenue) {
      return current
    }
    return { ...current, [key]: { tradeVenue: list.tradeVenue, reason } }
  }
  if (!existing) return current
  const next = { ...current }
  delete next[key]
  return next
}

/** `makerKey`s that have received at least one `RfqMakerRfqList` this subscription. */
export type MakerListsReceived = Record<string, true>

/** Record that the list's account answered the subscribe on its network (with or without a refusal). */
export function applyMakerListReceived(
  current: MakerListsReceived,
  list: MakerListKey
): MakerListsReceived {
  if (!list.accountId) return current
  const key = listKey(list)
  if (current[key]) return current
  return { ...current, [key]: true }
}

/**
 * The venue-side name takers see each account quote under, keyed by `makerKey`. A list
 * without `makerDisplayName` keeps the name already known (only some frames carry it).
 */
export function applyMakerDisplayName(
  current: Record<string, string>,
  list: MakerListKey & Pick<RfqMakerRfqList, 'makerDisplayName'>
): Record<string, string> {
  const name = list.makerDisplayName?.trim()
  if (!list.accountId || !name) return current
  const key = listKey(list)
  if (current[key] === name) return current
  return { ...current, [key]: name }
}

const NOT_APPROVED_PREFIX = 'not an approved rfq maker on'

export type MakerUnavailableKind = 'not-approved' | 'unavailable'

/**
 * The backend prefixes a venue's "you are not a maker" refusal with
 * `not an approved RFQ maker on <Venue>: `; any other reason is a temporary outage.
 */
export function classifyMakerUnavailableReason(reason: string): MakerUnavailableKind {
  return reason.trim().toLowerCase().startsWith(NOT_APPROVED_PREFIX)
    ? 'not-approved'
    : 'unavailable'
}

export type MakerAccountStatus =
  | { kind: 'live' }
  | { kind: 'checking' }
  | { kind: 'not-approved'; reason: string }
  | { kind: 'unavailable'; reason: string }
  | { kind: 'unsupported' }

export interface MakerStatusAccount {
  accountId: string
  name: string
  tradeVenue: TradeVenue
  status: AccountStatus
}

export interface MakerAccountStatusEntry {
  /** `makerKey(accountId, network)`. */
  key: string
  accountId: string
  name: string
  tradeVenue: TradeVenue
  network: RfqNetwork
  status: MakerAccountStatus
}

/** Status of one account on one maker network (`makerKey`). A refusal wins over "received a list". */
export function makerAccountStatus(
  key: string,
  unavailable: Record<string, MakerUnavailable>,
  received: MakerListsReceived
): MakerAccountStatus {
  const refusal = unavailable[key]
  if (refusal)
    return { kind: classifyMakerUnavailableReason(refusal.reason), reason: refusal.reason }
  return received[key] ? { kind: 'live' } : { kind: 'checking' }
}

/**
 * One entry per ACTIVE account on its own venue's network, in the given order, each
 * followed by a Paradigm entry once that account has heard from Paradigm (a linked desk).
 */
export function deriveMakerAccountStatuses(
  accounts: readonly MakerStatusAccount[],
  unavailable: Record<string, MakerUnavailable>,
  received: MakerListsReceived
): MakerAccountStatusEntry[] {
  return accounts
    .filter((account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE)
    .flatMap((account) => {
      const entry = (network: RfqNetwork, status?: MakerAccountStatus) => {
        const key = makerKey(account.accountId, network)
        return {
          key,
          accountId: account.accountId,
          name: account.name,
          tradeVenue: account.tradeVenue,
          network,
          status: status ?? makerAccountStatus(key, unavailable, received),
        }
      }
      const native = nativeMakerNetwork(account.tradeVenue)
      if (!venueSupportsRfqMaker(account.tradeVenue))
        return [entry(native, { kind: 'unsupported' })]
      const paradigm = makerKey(account.accountId, RfqNetwork.RFQ_NETWORK_PARADIGM)
      return received[paradigm] || unavailable[paradigm]
        ? [entry(native), entry(RfqNetwork.RFQ_NETWORK_PARADIGM)]
        : [entry(native)]
    })
}

// ─── Own quotes ────────────────────────────────────────────────────────────

export function isTerminalQuoteState(state: RfqMakerQuoteState): boolean {
  return (
    state === RfqMakerQuoteState.RFQ_MAKER_QUOTE_STATE_FILLED ||
    state === RfqMakerQuoteState.RFQ_MAKER_QUOTE_STATE_CANCELLED ||
    state === RfqMakerQuoteState.RFQ_MAKER_QUOTE_STATE_EXPIRED ||
    state === RfqMakerQuoteState.RFQ_MAKER_QUOTE_STATE_REJECTED ||
    state === RfqMakerQuoteState.RFQ_MAKER_QUOTE_STATE_NOT_FILLED
  )
}

const FILLED = RfqMakerQuoteState.RFQ_MAKER_QUOTE_STATE_FILLED

/**
 * Whether `frame` should replace `existing` for the same quote_id.
 *
 * - Newer stamp wins while the quote is not terminal.
 * - Once terminal, the first terminal state wins and a later non-terminal frame is ignored
 *   (a late OPEN re-emit must not resurrect a dead quote).
 * - FILLED is the exception: a fill is a fact, so FILLED replaces any other terminal state
 *   (and OPEN) even with an older stamp, and a newer FILLED frame updates `filledAmount`.
 */
export function shouldReplaceQuote(existing: RfqMakerQuote, frame: RfqMakerQuote): boolean {
  const newer = stampOf(frame.updatedAtMs) > stampOf(existing.updatedAtMs)
  if (frame.state === FILLED) return existing.state !== FILLED || newer
  if (existing.state === FILLED) return false
  if (isTerminalQuoteState(existing.state)) return false
  return newer
}

/** Terminal quotes kept per account (newest first). OPEN quotes are never dropped. */
export const MAX_TERMINAL_QUOTES_PER_ACCOUNT = 500

/**
 * Merge quote frames by quote_id (see `shouldReplaceQuote`). `requestedPrice` is carried
 * over from the previous frame when the new one omits it: some venue frames leave it out,
 * and the contract says to keep the last non-empty value per quote_id.
 */
export function upsertMakerQuotes(
  current: Record<string, RfqMakerQuote>,
  incoming: readonly RfqMakerQuote[]
): Record<string, RfqMakerQuote> {
  let next: Record<string, RfqMakerQuote> | null = null
  for (const frame of incoming) {
    if (!frame.quoteId) continue
    const existing = (next ?? current)[frame.quoteId]
    if (!existing) {
      next ??= { ...current }
      next[frame.quoteId] = frame
      continue
    }
    if (shouldReplaceQuote(existing, frame)) {
      next ??= { ...current }
      next[frame.quoteId] = frame.requestedPrice
        ? frame
        : { ...frame, requestedPrice: existing.requestedPrice }
      continue
    }
    // Not a state change, but a frame may still be the first to name the typed price.
    if (frame.requestedPrice && !existing.requestedPrice) {
      next ??= { ...current }
      next[frame.quoteId] = { ...existing, requestedPrice: frame.requestedPrice }
    }
  }
  return next ? capTerminalQuotes(next) : current
}

export function capTerminalQuotes(
  quotes: Record<string, RfqMakerQuote>,
  max = MAX_TERMINAL_QUOTES_PER_ACCOUNT
): Record<string, RfqMakerQuote> {
  const terminalByAccount = new Map<string, RfqMakerQuote[]>()
  for (const quote of Object.values(quotes)) {
    if (!isTerminalQuoteState(quote.state)) continue
    const list = terminalByAccount.get(quote.accountId) ?? []
    list.push(quote)
    terminalByAccount.set(quote.accountId, list)
  }
  let result: Record<string, RfqMakerQuote> | null = null
  for (const list of terminalByAccount.values()) {
    if (list.length <= max) continue
    list.sort((a, b) => compareStampsDesc(a.updatedAtMs, b.updatedAtMs))
    result ??= { ...quotes }
    for (const dropped of list.slice(max)) delete result[dropped.quoteId]
  }
  return result ?? quotes
}

// ─── Sending ───────────────────────────────────────────────────────────────

/** One quote per side per RFQ per account — the contract's upsert identity. */
export function quoteSlotKey(slot: { accountId: string; rfqId: string; side: RfqQuoteSide }) {
  return `${slot.accountId}|${slot.rfqId}|${slot.side}`
}

/**
 * A Send the server has routed (it returned a quote_id) whose result has not reached the
 * stream yet. `baseUpdatedAtMs` is the quote's stamp at send time (absent for a new quote):
 * any newer frame for that quote_id settles it.
 */
export interface PendingMakerQuote {
  quoteId: string
  accountId: string
  rfqId: string
  side: RfqQuoteSide
  price: string
  sentAtMs: number
  baseUpdatedAtMs?: string
}

/** After this long with no stream frame, a pending Send stops showing "sending…". */
export const PENDING_QUOTE_TIMEOUT_MS = 15_000

export function isPendingSettled(
  pending: PendingMakerQuote,
  quotes: Record<string, RfqMakerQuote>
): boolean {
  const quote = quotes[pending.quoteId]
  if (!quote) return false
  if (pending.baseUpdatedAtMs === undefined) return true
  return stampOf(quote.updatedAtMs) > stampOf(pending.baseUpdatedAtMs)
}

/** Drop pending Sends the stream has answered. Same reference when nothing settled. */
export function prunePendingQuotes(
  pending: Record<string, PendingMakerQuote>,
  quotes: Record<string, RfqMakerQuote>
): Record<string, PendingMakerQuote> {
  let next: Record<string, PendingMakerQuote> | null = null
  for (const [key, entry] of Object.entries(pending)) {
    if (!isPendingSettled(entry, quotes)) continue
    next ??= { ...pending }
    delete next[key]
  }
  return next ?? pending
}

/** Whether a Send for this quote is still waiting on the stream (shows "sending…"). */
export function isQuoteSending(
  pending: PendingMakerQuote | undefined,
  quotes: Record<string, RfqMakerQuote>,
  nowMs: number
): boolean {
  if (!pending) return false
  if (nowMs - pending.sentAtMs > PENDING_QUOTE_TIMEOUT_MS) return false
  return !isPendingSettled(pending, quotes)
}

/**
 * The quote_id a Send for this slot must carry.
 *
 * - The slot's OPEN quote: re-price it (omitting the id would place a second quote).
 * - Else a routed Send whose quote has not reached the stream yet: a retry must keep its id.
 * - Else nothing: no quote yet, or the last one is terminal (EXPIRED, CANCELLED, FILLED,
 *   NOT_FILLED, REJECTED), which the server refuses to upsert — a new quote omits the id.
 */
export function resolveSendQuoteId(args: {
  accountId: string
  rfqId: string
  side: RfqQuoteSide
  quotes: Record<string, RfqMakerQuote>
  pending: Record<string, PendingMakerQuote>
}): string | undefined {
  const { accountId, rfqId, side, quotes, pending } = args
  const open = findSlotQuote(
    quotes,
    { accountId, rfqId, side },
    (q) => !isTerminalQuoteState(q.state)
  )
  if (open) return open.quoteId
  const inFlight = pending[quoteSlotKey({ accountId, rfqId, side })]
  if (inFlight && !quotes[inFlight.quoteId]) return inFlight.quoteId
  return undefined
}

/** The newest quote in a slot matching `predicate`. */
export function findSlotQuote(
  quotes: Record<string, RfqMakerQuote>,
  slot: { accountId: string; rfqId: string; side: RfqQuoteSide },
  predicate: (quote: RfqMakerQuote) => boolean = () => true
): RfqMakerQuote | undefined {
  let best: RfqMakerQuote | undefined
  for (const quote of Object.values(quotes)) {
    if (quote.accountId !== slot.accountId || quote.rfqId !== slot.rfqId) continue
    if (quote.side !== slot.side || !predicate(quote)) continue
    if (!best || stampOf(quote.updatedAtMs) > stampOf(best.updatedAtMs)) best = quote
  }
  return best
}

// ─── Price ─────────────────────────────────────────────────────────────────

/** Significant digits a rust_decimal `Decimal` holds without rounding. */
const MAX_DECIMAL_DIGITS = 28
const WIRE_PRICE_RE = /^-?[0-9]+(\.[0-9]+)?$/

export type MakerPriceResult =
  | { ok: true; price: string }
  | { ok: false; reason: 'empty' | 'format' | 'precision' }

/**
 * Turn what the user typed into the wire price: a plain decimal matching
 * `^-?[0-9]+(\.[0-9]+)?$` that fits a 28-digit Decimal. Thousands separators, surrounding
 * spaces, a bare ".5" and a trailing "5." are tidied rather than refused; leading zeros are
 * dropped and "-0" becomes "0". Exponents, "+" and anything else are refused.
 */
export function normalizeMakerPrice(input: string): MakerPriceResult {
  let text = input.trim().replace(/,/g, '')
  if (text === '' || text === '-') return { ok: false, reason: 'empty' }
  const negative = text.startsWith('-')
  if (negative) text = text.slice(1)
  if (text.startsWith('.')) text = `0${text}`
  if (text.endsWith('.')) text = text.slice(0, -1)
  if (!WIRE_PRICE_RE.test(text) || text.startsWith('-')) return { ok: false, reason: 'format' }

  const [rawInt, frac = ''] = text.split('.')
  const int = rawInt.replace(/^0+(?=\d)/, '')
  const significant = (int === '0' ? '' : int) + frac
  const digits = int === '0' ? significant.replace(/^0+/, '') : significant
  if (frac.length > MAX_DECIMAL_DIGITS || digits.length > MAX_DECIMAL_DIGITS) {
    return { ok: false, reason: 'precision' }
  }
  const body = frac ? `${int}.${frac}` : int
  const isZero = /^0(\.0+)?$/.test(body)
  return { ok: true, price: negative && !isZero ? `-${body}` : body }
}

/** Numeric equality of two decimal strings ("1.50" == "1.5"); false if either is not one. */
export function samePrice(a: string | undefined, b: string | undefined): boolean {
  if (a === undefined || b === undefined) return false
  const na = normalizeMakerPrice(a)
  const nb = normalizeMakerPrice(b)
  if (!na.ok || !nb.ok) return false
  const strip = (p: string) => (p.includes('.') ? p.replace(/0+$/, '').replace(/\.$/, '') : p)
  return strip(na.price) === strip(nb.price)
}

// ─── Quotability ───────────────────────────────────────────────────────────

export type MakerQuoteBlock = 'closed' | 'quote-amount' | 'unavailable'

/**
 * Why this RFQ cannot be quoted from this account right now, or null when it can.
 * A Lighter RFQ sized only in quote currency arrives with an empty `amount`: it is shown
 * but not quotable (the venue service refuses it with "quote-amount RFQ").
 */
export function makerQuoteBlock(
  rfq: RfqMakerRfq,
  nowMs: number,
  unavailable: Record<string, MakerUnavailable>
): MakerQuoteBlock | null {
  if (!isMakerRfqLive(rfq, nowMs)) return 'closed'
  if (unavailable[makerKeyOf(rfq)]) return 'unavailable'
  if (!rfq.amount.trim()) return 'quote-amount'
  return null
}

// ─── Grouping ──────────────────────────────────────────────────────────────

/** One venue RFQ as the blotter lists it, with every account of this user that sees it. */
export interface MakerRfqGroup {
  rfqId: string
  /** The most recently updated account's copy; the venue RFQ is the same for all of them. */
  rfq: RfqMakerRfq
  accountIds: string[]
}

/**
 * Group the per-account RFQ rows by rfq_id (a user with two Deribit maker accounts sees each
 * venue RFQ twice), keeping only rows that pass `include`. Account ids come back sorted.
 */
export function groupMakerRfqs(
  rfqs: Record<string, RfqMakerRfq>,
  include: (rfq: RfqMakerRfq) => boolean = () => true
): MakerRfqGroup[] {
  const groups = new Map<string, MakerRfqGroup>()
  for (const rfq of Object.values(rfqs)) {
    if (!include(rfq)) continue
    const group = groups.get(rfq.rfqId)
    if (!group) {
      groups.set(rfq.rfqId, { rfqId: rfq.rfqId, rfq, accountIds: [rfq.accountId] })
      continue
    }
    group.accountIds.push(rfq.accountId)
    if (stampOf(rfq.updatedAtMs) > stampOf(group.rfq.updatedAtMs)) group.rfq = rfq
  }
  for (const group of groups.values()) group.accountIds.sort()
  return [...groups.values()]
}
